import React, { useState } from "react";
import Reveal from "./Reveal";
import { Phone, Instagram, MapPin } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { ASSETS, CONTACT, INTEREST_OPTIONS } from "../mock";

const inputClass =
  "w-full bg-transparent border-b border-[#B88A3B]/30 focus:border-[#B88A3B] outline-none py-3 text-[#F9F9F7] placeholder:text-[#F9F9F7]/35 transition-colors duration-300";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: INTEREST_OPTIONS[0],
    message: "",
  });

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Enquiry sent",
      description: "Thank you. Our team will be in touch with you shortly.",
    });
    setForm({ name: "", email: "", phone: "", interest: INTEREST_OPTIONS[0], message: "" });
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#081F3A] text-[#F9F9F7] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <p className="uppercase tracking-[0.3em] text-xs font-normal text-[#B88A3B]">
            Contact
          </p>
          <h2 className="font-serif-lux font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-6">
            Begin your <span className="gold-text italic">Marbella story</span>
          </h2>
          <p className="text-base text-[#F9F9F7]/60 leading-relaxed mt-8 max-w-md">
            Tell us what you envision: a home, an investment, or an unforgettable
            season on the Costa del Sol.
          </p>
          <div className="mt-12 space-y-6">
            <a href={CONTACT.phoneHref} className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full border border-[#B88A3B]/40 flex items-center justify-center shrink-0">
                <Phone size={18} strokeWidth={1.4} className="text-[#B88A3B]" />
              </span>
              <span className="text-lg text-[#F9F9F7]/85 group-hover:text-[#B88A3B] transition-colors duration-300">
                {CONTACT.phone}
              </span>
            </a>
            <a
              href={CONTACT.instagramHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 rounded-full border border-[#B88A3B]/40 flex items-center justify-center shrink-0">
                <Instagram size={18} strokeWidth={1.4} className="text-[#B88A3B]" />
              </span>
              <span className="text-lg text-[#F9F9F7]/85 group-hover:text-[#B88A3B] transition-colors duration-300">
                {CONTACT.instagram}
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-full border border-[#B88A3B]/40 flex items-center justify-center shrink-0">
                <MapPin size={18} strokeWidth={1.4} className="text-[#B88A3B]" />
              </span>
              <span className="text-lg text-[#F9F9F7]/85">{CONTACT.location}</span>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-x-10 gap-y-8 lg:pt-8">
            <input name="name" value={form.name} onChange={handleChange} className={inputClass} placeholder="Full name" required />
            <input name="email" type="email" value={form.email} onChange={handleChange} className={inputClass} placeholder="Email address" required />
            <input name="phone" value={form.phone} onChange={handleChange} className={inputClass} placeholder="Phone (optional)" />
            <select name="interest" value={form.interest} onChange={handleChange} className={`${inputClass} appearance-none cursor-pointer [&>option]:bg-[#0B284A]`}>
              {INTEREST_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <textarea name="message" value={form.message} onChange={handleChange} className={`${inputClass} sm:col-span-2 resize-none`} rows={4} placeholder="Tell us about your plans..." required />
            <button
              type="submit"
              className="sm:col-span-2 justify-self-start bg-[#B88A3B] text-white px-10 py-4 uppercase tracking-[0.2em] text-sm font-medium hover:bg-[#C09245] hover:-translate-y-1 transition-[background-color,transform] duration-300 inline-flex items-center gap-3"
            >
              Send Enquiry
            </button>
          </form>
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-24">
        <Reveal>
          <p className="uppercase tracking-[0.3em] text-xs font-normal text-[#B88A3B]">
            Payment Method
          </p>
          <div className="mt-8">
            <label className="flex items-center gap-3 cursor-pointer group w-fit">
              <input className="sr-only" type="radio" value="qr" defaultChecked name="payment-method" />
              <span className="w-5 h-5 rounded-full border flex items-center justify-center transition-colors duration-300 border-[#B88A3B]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#B88A3B]" />
              </span>
              <span className="text-base transition-colors duration-300 text-[#B88A3B]">
                Pay through QR Code
              </span>
            </label>
          </div>
          <div className="mt-10 border border-[#B88A3B]/20 p-8 lg:p-10 max-w-2xl flex flex-col sm:flex-row items-center gap-8">
            <img
              alt="Revolut payment QR code for Marbella Consulting"
              className="w-56 h-auto object-contain rounded-xl"
              src={ASSETS.revolutQr}
            />
            <p className="text-base text-[#F9F9F7]/70 leading-relaxed text-center sm:text-left">
              Scan the QR code with your banking app to pay Marbella Consulting
              securely via Revolut.
            </p>
          </div>
          <div className="mt-10 border border-[#B88A3B]/20 p-8 lg:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
            <div>
              <p className="uppercase tracking-[0.3em] text-xs font-normal text-[#B88A3B]">
                Payments &amp; Reservations
              </p>
              <p className="text-base text-[#F9F9F7]/70 leading-relaxed mt-4 max-w-xl">
                Payment details are shared privately once your booking is
                confirmed. Send us an enquiry and our team will guide you through
                every step.
              </p>
            </div>
            <a
              href="#contact"
              className="shrink-0 bg-[#B88A3B] text-white px-8 py-4 uppercase tracking-[0.2em] text-sm font-medium hover:bg-[#C09245] hover:-translate-y-1 transition-[background-color,transform] duration-300"
            >
              Enquire on Request
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
